import React from 'react';
import { useTranslation } from 'react-i18next';
import useClasses from '../../hooks/useClasses';

import './style.scss';

type Props = Omit<
React.DetailedHTMLProps<
React.HTMLAttributes<HTMLDivElement>,
HTMLDivElement
> & {
  onClose: () => void;
  cta?: () => React.ReactElement;
  title?: string;
  visuallyHideTitle?: boolean;
  label?: string;
  showCloseButton?: boolean;
  closeButtonText?: string;
},
'ref'
>;

/*
 * Stateless popup component for use with dialog-like controls.
 * Use in combination with usePopup in a containing component.
 * Prefer to provide a title rather than a label (or aria-label)
 * due to usability issues w.r.t. aria-label.
 * See https://www.w3.org/WAI/tutorials/forms/labels/
 * If you want to hide a title, add the visuallyHideTitle prop
 */

const Popup = React.forwardRef<HTMLDivElement, Props>(
  ({
    className,
    children,
    cta,
    id,
    title,
    visuallyHideTitle,
    label,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    onClose,
    showCloseButton = true,
    closeButtonText,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    const { buildClass, joinClasses } = useClasses('Popup');
    const titleId = React.useMemo(() => {
      const TITLE_ID_SUFFIX = '_popup_title';

      if (title && id) {
        return `${id}${TITLE_ID_SUFFIX}`;
      }

      if (title) {
        const snakeCaseTitle = title.toLowerCase().replace(' ', '_');

        return `${snakeCaseTitle}${TITLE_ID_SUFFIX}`;
      }

      return '';
    }, [title, id]);

    return (
      <div className={buildClass('overlay')}>
        <div
          id={id}
          className={joinClasses(buildClass(), className)}
          role="dialog"
          aria-modal
          ref={ref}
          aria-label={label || ariaLabel}
          aria-labelledby={titleId || ariaLabelledBy}
          {...props}
        >
          {title && (
            <div className={buildClass('header', {
              'visually-hidden': visuallyHideTitle,
            })}
            >
              <h2
                className={joinClasses(buildClass('heading'), 'asH3')}
                id={titleId}
              >{title}
              </h2>
            </div>
          )}
          <div className={joinClasses(buildClass('body'), 'u-word-break')}>
            {children}
          </div>
          <div className={joinClasses(buildClass('footer'), 'mt-4')}>
            {cta && cta()}
            {showCloseButton && (
              <button
                type="button"
                className={buildClass('close')}
                onClick={onClose}
              >
                {closeButtonText || t('buttons.close')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default Popup;
